import * as React from 'react';
import styled from 'styled-components';
import ReactInputMask from 'react-input-mask';
import AddIcon from '@material-ui/icons/Add';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';

import { US_STATES } from '../constants/usStates';
import { ButtonSecondary } from '../atoms';
import isYupFieldRequired from '../utils/isYupFieldRequired';
import { FormServerError } from '../types';
import {
  InputAutocompletePlaces,
  InputTextWithLabel,
  InputBaseContainer,
  InputSelectWithLabel,
  TextAreaWithLabel,
  InputCurrencyWithLabel,
  InputDatePickerWithLabel,
  ButtonWithLoader,
} from '../molecules';
import {
  emailRegex,
  phoneRegex,
  zipCode,
  goalRegex,
} from '../regex';
import {
  FranchiseType,
  FranchiseFormType,
} from '../api/franchise';
import transformServerErrorsToForm from '../utils/transformServerErrorsToForm';
import useUserRole from '../hooks/useUserRole';
import { DEFAULT_DATE_FORMAT } from '../constants';
import parseUTCDate from '../utils/parseUTCDate';
import formatDate from '../utils/formatDate';
import { formatToFormData } from '../utils';

import {
  FRANCHISE_LEVEL_DATA,
  FRANCHISE_TYPE_DATA,
} from '../__DATA__';
import FranchiseQRCode from './FranchiseQRCode';

const labels = {
  name: 'Branch Name',
  prefix: 'Branch Prefix',
  franchiseLevel: 'Branch Level',
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  phoneNumber: 'Phone',
  contactPerson: 'Contact Person',
  franchiseType: 'Branch Type',
  yearlyGoal: 'Projected yearly Goal',
  note: 'Notes',
  address: 'Address',
  street: 'Street',
  city: 'City',
  state: 'State',
  zipCode: 'Zip',
  toDate: 'To date',
  fromDate: 'From date',
};

const schema = yup.object().shape({
  name: yup.string().required().label(labels.name),
  // EA-1376: short branch prefix (e.g. NPL). Required; editable after creation.
  prefix: yup
    .string()
    .trim()
    .required()
    .max(10, 'Prefix must be 10 characters or fewer')
    .label(labels.prefix),
  franchiseLevel: yup
    .object()
    .shape({
      value: yup.string(),
      label: yup.string(),
    })
    .required()
    .typeError('Select a franchise level from the list')
    .label(labels.franchiseLevel),
  firstName: yup
    .string()
    .required()
    .label(labels.firstName),
  lastName: yup.string().required().label(labels.lastName),
  email: yup
    .string()
    .matches(emailRegex, 'Enter a valid email address')
    .required()
    .label(labels.email),
  phoneNumber: yup
    .string()
    .matches(
      phoneRegex,
      'Invalid phone number. The phone number must be in format 2XX-XXX-XXXX',
    )
    .required()
    .label(labels.phoneNumber),
  contactPerson: yup
    .string()
    .required()
    .label(labels.contactPerson),
  franchiseType: yup
    .object()
    .shape({
      value: yup.string(),
      label: yup.string(),
    })
    .required()
    .typeError('Select a franchise level from the list')
    .nullable()
    .label(labels.franchiseType),
  yearlyGoal: yup
    .string()
    .matches(
      goalRegex,
      'Invalid goal. The goal must include only numbers',
    )
    .required()
    .label(labels.yearlyGoal),
  note: yup.string().label(labels.note),
  address: yup.string().required().label(labels.address),
  state: yup.string().label(labels.state),
  street: yup.string().label(labels.street),
  city: yup.string().label(labels.city),
  zipCode: yup
    .string()
    .matches(
      zipCode,
      'Invalid Code number. The code number must be in format 55555 or 55555-5555',
    )
    .label(labels.zipCode),
  toDate: yup
    .date()
    .required()
    .typeError('Invalid or absent date')
    .label(labels.toDate),
  fromDate: yup
    .date()
    .required()
    .typeError('Invalid or absent date')
    .label(labels.fromDate),
});

const FranchiseForm = ({
  onSubmit,
  initialValues,
  modalError,
  pushToSettingsPage,
  addButtonText = 'Add new item',
  updateButtonText = 'Update',
  initialErrors,
}: Props) => {
  const fromDateUTC = parseUTCDate(
    initialValues?.fromDate as string,
  );
  const toDateUTC = parseUTCDate(
    initialValues?.toDate as string,
  );

  const {
    handleSubmit,
    setError,
    errors,
    register,
    control,
    setValue,
    formState,
  } = useForm<FranchiseType>({
    resolver: yupResolver(schema),
    defaultValues: initialValues
      ? {
          ...initialValues,
          fromDate: fromDateUTC,
          toDate: toDateUTC,
          franchiseLevel: initialValues.franchiseLevel
            ? FRANCHISE_LEVEL_DATA.filter(
                item =>
                  item.value ===
                  initialValues.franchiseLevel,
              )[0]
            : '',
          franchiseType: initialValues.franchiseType
            ? FRANCHISE_TYPE_DATA.filter(
                item =>
                  item.value ===
                  initialValues.franchiseType,
              )[0]
            : '',
        }
      : ({} as FranchiseType),
  });
  const { isSuperAdmin } = useUserRole();

  const isDeleted = initialValues?.isDeleted;

  const [isSubmitting, setIsSubmitting] = React.useState<
    boolean
  >(false);
  const [emailBlurError, setEmailBlurError] = React.useState('');
  const [qrFile, setQrFile] = React.useState<File | null>(null);

  React.useEffect(() => {
    if (
      formState.isSubmitting &&
      !Object.keys(errors).length
    ) {
      setIsSubmitting(formState.isSubmitting);
    }
  }, [formState]);

  React.useEffect(() => {
    if (initialErrors) {
      transformServerErrorsToForm<FranchiseType>(
        initialErrors,
      ).forEach(({ name, types }) =>
        setError(name, { types }));
    }
  }, [initialErrors]);

  React.useEffect(() => {
    if (Object.keys(errors).length) {
      setIsSubmitting(false);
    }
  }, [errors, initialErrors]);

  const middleware = (
    submitFn: (data: FranchiseType | FormData) => void,
  ) => (data: FranchiseFormType) => {
    const normalized = {
      ...data,
      id: initialValues?.id,
      fromDate: formatDate(data.fromDate),
      toDate: formatDate(data.toDate),
      franchiseLevel: data.franchiseLevel?.value,
      franchiseType: data.franchiseType?.value,
    };

    if (qrFile) {
      const fd = formatToFormData(normalized) as FormData;
      fd.append('qr_code', qrFile, qrFile.name);
      submitFn(fd);
      return;
    }

    submitFn(normalized);
  };

  const onCurrencyChange = (value, name) => {
    setValue(name, value);
  };

  const handlePlaceSelect = (address, isSelect) => {
    if (isSelect) {
      return Object.entries(address).map(([key, value]) => {
        if (key === 'state') {
          const match = US_STATES.find(
            s => s.label.toLowerCase() === String(value).toLowerCase() ||
              s.value.toLowerCase() === String(value).toLowerCase(),
          );
          return setValue(key, match?.value || value);
        }
        return setValue(key, value);
      });
    }
    return setValue('address', address);
  };

  return (
    <Container
      disabled={isDeleted || (isSubmitting && !modalError)}
    >
      <FormContainer
        autoComplete="off"
        aria-autocomplete="none"
        onSubmit={handleSubmit(middleware(onSubmit))}
      >
        <Columns>
          <Column>
        <InputBaseContainer>
          <InputTextWithLabel
            name="name"
            error={errors.name}
            inputRef={register}
            label={labels.name}
            InputProps={{
              readOnly: !isSuperAdmin,
            }}
            isRequired={isYupFieldRequired('name', schema)}
          />
          <InputTextWithLabel
            name="prefix"
            error={errors.prefix}
            inputRef={register}
            label={labels.prefix}
            placeholder="e.g. NPL"
            InputProps={{
              readOnly: !isSuperAdmin,
            }}
            isRequired={isYupFieldRequired('prefix', schema)}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <Controller
            name="franchiseLevel"
            defaultValue=""
            control={control}
            render={props => (
              <InputSelectWithLabel
                placeholder=""
                menuPlacement="bottom"
                label={labels.franchiseLevel}
                error={errors.franchiseLevel}
                isDisabled={!isSuperAdmin}
                options={FRANCHISE_LEVEL_DATA}
                isRequired={isYupFieldRequired(
                  'franchiseLevel',
                  schema,
                )}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <InputTextWithLabel
            name="firstName"
            error={errors.firstName}
            inputRef={register}
            label={labels.firstName}
            InputProps={{
              readOnly: !isSuperAdmin,
            }}
            isRequired={isYupFieldRequired(
              'firstName',
              schema,
            )}
          />
          <InputTextWithLabel
            name="lastName"
            error={errors.lastName}
            inputRef={register}
            label={labels.lastName}
            InputProps={{
              readOnly: !isSuperAdmin,
            }}
            isRequired={isYupFieldRequired(
              'lastName',
              schema,
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <InputTextWithLabel
            name="email"
            type="email"
            error={errors.email || (emailBlurError ? { message: emailBlurError } : undefined)}
            inputRef={register}
            label={labels.email}
            placeholder="user@example.com"
            InputProps={{
              readOnly: !isSuperAdmin,
            }}
            isRequired={isYupFieldRequired('email', schema)}
            onBlur={e => {
              const val = e.target.value;
              if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
                setEmailBlurError('Please enter a valid email address');
              } else {
                setEmailBlurError('');
              }
            }}
            onChange={() => { if (emailBlurError) setEmailBlurError(''); }}
          />
          <Controller
            control={control}
            defaultValue=""
            name="phoneNumber"
            render={props => (
              <ReactInputMask
                mask="999-999-9999"
                maskChar=" "
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              >
                {() => (
                  <InputTextWithLabel
                    error={errors.phoneNumber}
                    label={labels.phoneNumber}
                    isRequired={isYupFieldRequired(
                      'phoneNumber',
                      schema,
                    )}
                  />
                )}
              </ReactInputMask>
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <InputTextWithLabel
            name="contactPerson"
            error={errors.contactPerson}
            inputRef={register}
            label={labels.contactPerson}
            InputProps={{
              readOnly: !isSuperAdmin,
            }}
            isRequired={isYupFieldRequired(
              'contactPerson',
              schema,
            )}
          />
          <Controller
            name="franchiseType"
            defaultValue=""
            control={control}
            render={props => (
              <InputSelectWithLabel
                placeholder=""
                menuPlacement="bottom"
                label={labels.franchiseType}
                error={errors.franchiseType}
                isDisabled={!isSuperAdmin}
                options={FRANCHISE_TYPE_DATA}
                isRequired={isYupFieldRequired(
                  'franchiseType',
                  schema,
                )}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <Controller
            name="fromDate"
            defaultValue=""
            valueName="selected"
            onChange={([selected]) => selected}
            control={control}
            render={props => (
              <InputDatePickerWithLabel
                label={labels.fromDate}
                error={errors.fromDate}
                disabled={!isSuperAdmin}
                dateFormat={DEFAULT_DATE_FORMAT}
                isRequired={isYupFieldRequired(
                  'fromDate',
                  schema,
                )}
                // eslint-disable-next-line react/prop-types
                selected={props.value}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
          <Controller
            name="toDate"
            defaultValue=""
            valueName="selected"
            onChange={([selected]) => selected}
            control={control}
            render={props => (
              <InputDatePickerWithLabel
                label={labels.toDate}
                error={errors.toDate}
                disabled={!isSuperAdmin}
                dateFormat={DEFAULT_DATE_FORMAT}
                isRequired={isYupFieldRequired(
                  'toDate',
                  schema,
                )}
                // eslint-disable-next-line react/prop-types
                selected={props.value}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <SplitRow>
            <Controller
              name="yearlyGoal"
              defaultValue=""
              control={control}
              render={props => (
                <InputCurrencyWithLabel
                  allowDecimals={false}
                  error={errors.yearlyGoal}
                  label={labels.yearlyGoal}
                  onCurrencyChange={onCurrencyChange}
                  isRequired={isYupFieldRequired(
                  'yearlyGoal',
                  schema,
                )}
                  disabled={!isSuperAdmin}
                // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                />
            )}
            />
            <FranchiseQRCode
              initialValues={initialValues}
              editable={isSuperAdmin}
              onFileSelect={setQrFile}
            />
          </SplitRow>
        </InputBaseContainer>
          </Column>
          <Column>
        <InputBaseContainer>
          <TextAreaWithLabel
            name="note"
            error={errors.note}
            label={labels.note}
            disabled={!isSuperAdmin}
            inputRef={register as any}
            isRequired={isYupFieldRequired('note', schema)}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <Controller
            name="address"
            defaultValue=""
            control={control}
            render={props => (
              <InputAutocompletePlaces
                error={errors.address}
                label={labels.address}
                isDisabled={!isSuperAdmin}
                handleFieldChange={handlePlaceSelect}
                isRequired={isYupFieldRequired(
                  'address',
                  schema,
                )}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <InputTextWithLabel
            name="street"
            error={errors.street}
            inputRef={register}
            label={labels.street}
            isRequired={isYupFieldRequired(
              'street',
              schema,
            )}
            InputProps={{
              readOnly: !isSuperAdmin,
            }}
          />
          <InputTextWithLabel
            name="city"
            error={errors.city}
            inputRef={register}
            label={labels.city}
            isRequired={isYupFieldRequired('city', schema)}
            InputProps={{
              readOnly: !isSuperAdmin,
            }}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <Controller
            name="state"
            defaultValue=""
            control={control}
            render={({ onChange, onBlur, value }) => {
              const selected = US_STATES.find(
                s => s.value === value || s.label === value,
              ) || null;
              return (
                <InputSelectWithLabel
                  placeholder="Select State"
                  label={labels.state}
                  error={errors.state}
                  isRequired={isYupFieldRequired(
                    'state',
                    schema,
                  )}
                  isSearchable
                  isDisabled={!isSuperAdmin}
                  options={US_STATES}
                  value={selected}
                  onBlur={onBlur}
                  onChange={opt => onChange(opt?.value || '')}
                />
              );
            }}
          />
          <Controller
            name="zipCode"
            defaultValue=""
            control={control}
            render={({ onChange, onBlur, value }) => (
              <InputTextWithLabel
                name="zipCode"
                label={labels.zipCode}
                error={errors.zipCode}
                isRequired={isYupFieldRequired(
                  'zipCode',
                  schema,
                )}
                InputProps={{
                  readOnly: !isSuperAdmin,
                }}
                value={value}
                onBlur={onBlur}
                onChange={e => {
                  const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 9);
                  let formatted = digits;
                  if (digits.length > 5) {
                    formatted = `${digits.slice(0, 5)}-${digits.slice(5)}`;
                  }
                  onChange(formatted);
                }}
                inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                placeholder="55555 or 55555-5555"
              />
            )}
          />
        </InputBaseContainer>
          </Column>
        </Columns>
        {initialValues && isSuperAdmin && (
          <ConfigurationButtonContainer>
            <ButtonSecondary onClick={pushToSettingsPage}>
              <AddIcon /> Add configuration
            </ButtonSecondary>
          </ConfigurationButtonContainer>
        )}
        {!isDeleted && (
          <ButtonWithLoader
            disabled={isSubmitting && !modalError}
            loading={isSubmitting && !modalError}
            type="submit"
          >
            {initialValues
              ? updateButtonText
              : addButtonText}
          </ButtonWithLoader>
        )}
      </FormContainer>
    </Container>
  );
};

const SplitRow = styled.div`
 display: grid;
 grid-template-columns: minmax(0, 1fr) 160px;
 gap: 16px;
 align-items: start;

 @media (max-width: 540px) {
   grid-template-columns: 1fr;
 }
`;

const Container = styled.fieldset`
  display: block;
  margin: 0;
  padding: 0;
  width: 100%;
  border: none;
  text-align: center;
`;

const FormContainer = styled.form`
  width: 100%;

  .MuiButton-root {
    margin-top: 24px;
  }
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 0 24px;
  width: 100%;
  box-sizing: border-box;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 0 8px;
  box-sizing: border-box;
`;

const ConfigurationButtonContainer = styled.div`
  text-align: start;
`;

interface Props {
  addButtonText?: string;
  updateButtonText?: string;
  initialValues?: FranchiseType;
  onSubmit: (data: FranchiseType | FormData) => void;
  pushToSettingsPage: () => void;
  initialErrors?: FormServerError<FranchiseType>;
  modalError: boolean;
}

export default FranchiseForm;
